import { batchFamilyRoot } from './batchProducts';
/**
 * Facility View drag & drop: where a batch can be dropped and what happens.
 * Pure functions so the routing is unit-tested without a canvas.
 */
import { Batch, DesignObject, Equipment } from '../types/interfaces';

export const BATCH_STAGES = [
  'Brewing',
  'Fermentation',
  'Aging',
  'Filtering/Carbonating',
  'Packaging',
  'Completed',
] as const;
export type BatchStage = (typeof BATCH_STAGES)[number];
/** Stages a batch can sit in while it is in a vessel (Packaging/Completed empty the vessel). */
export const VESSEL_STAGES: BatchStage[] = ['Brewing', 'Fermentation', 'Aging', 'Filtering/Carbonating'];

export const BLEND_BEFORE_PACKAGE = 'Blend into a brite tank before packaging.';

export type VesselKind = 'fermenter' | 'brite' | 'barrel' | 'other';

export function vesselKind(obj: DesignObject, equipment: Equipment[]): VesselKind {
  const eq = equipment.find((e) => Number(e.equipmentId) === Number(obj.equipmentId));
  const text = `${eq?.type || ''} ${eq?.name || ''} ${obj.type || ''} ${obj.abbreviation || ''} ${obj.name || ''}`;
  if (/bright|brite|\bBT\d*/i.test(text)) return 'brite';
  if (/ferment|\bFV\d*|unitank/i.test(text)) return 'fermenter';
  // BBL1–BBL11, ABBL12 (Athens), BR11, Aging Barrel, etc.
  if (/barrel|\bA?BBL\d*|\bBR\d+/i.test(text)) return 'barrel';
  return 'other';
}

const stageIndex = (s?: string | null) => (s ? BATCH_STAGES.indexOf(s as BatchStage) : -1);

/** Default stage for the target vessel, never earlier than the batch's current stage. */
export function defaultStageFor(kind: VesselKind, current?: string | null): BatchStage {
  const wanted: BatchStage =
    kind === 'brite'
      ? 'Filtering/Carbonating'
      : kind === 'fermenter'
        ? 'Fermentation'
        : kind === 'barrel'
          ? 'Aging'
          : ((current as BatchStage) || 'Fermentation');
  const cur = stageIndex(current);
  if (cur > stageIndex(wanted) && VESSEL_STAGES.includes(current as BatchStage)) return current as BatchStage;
  return wanted;
}

/** Stage choices for the move modal: current stage onward, vessel stages only. */
export function stageOptions(current?: string | null): BatchStage[] {
  const cur = Math.max(0, stageIndex(current));
  return VESSEL_STAGES.filter((s) => stageIndex(s) >= cur);
}

export type DropResult =
  | { kind: 'none' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'move'; stage: BatchStage; fromKind: VesselKind; toKind: VesselKind }
  /** Earlier stage / BT→FV: allowed only through the explicit Move back flow (confirm + reason). */
  | { kind: 'regress'; stage: BatchStage; fromKind: VesselKind; toKind: VesselKind }
  | { kind: 'package'; locationId: number };

export interface DropContext {
  equipment: Equipment[];
  /** Resolved inventory location for storage shapes (name/abbr fallback). */
  locationIdFor: (obj: DesignObject) => number | null;
  /** Storage shape linked to the taproom (sold, tax paid): never a packaging target. */
  isTaproomTarget?: (obj: DesignObject) => boolean;
}

const label = (o: DesignObject) => o.abbreviation || o.name || 'that shape';

export function classifyDrop(source: DesignObject, batch: Batch, target: DesignObject | null, ctx: DropContext): DropResult {
  if (!target || target.id === source.id) return { kind: 'none' };
  if (batch.siteId && (target as DesignObject & { siteId?: string }).siteId && (target as any).siteId !== batch.siteId) {
    return { kind: 'invalid', reason: `${label(target)} is at a different site` };
  }
  if (target.equipmentId) {
    const eq = ctx.equipment.find((e) => Number(e.equipmentId) === Number(target.equipmentId));
    if (eq && eq.siteId && batch.siteId && eq.siteId !== batch.siteId) {
      return { kind: 'invalid', reason: `${label(target)} is at a different site` };
    }
    // Barrels of one split family may blend into the same vessel (e.g. RBP080726.1–.10 → brite).
    const root = batchFamilyRoot(batch.batchId);
    const others = (target.batches || []).filter((b) => b.batchId !== batch.batchId && batchFamilyRoot(b.batchId) !== root);
    if (others.length) return { kind: 'invalid', reason: `${label(target)} is occupied by ${others[0].batchId}` };
    const fromKind = vesselKind(source, ctx.equipment);
    const toKind = vesselKind(target, ctx.equipment);
    const natural: BatchStage | null =
      toKind === 'brite'
        ? 'Filtering/Carbonating'
        : toKind === 'fermenter'
          ? 'Fermentation'
          : toKind === 'barrel'
            ? 'Aging'
            : null;
    if ((fromKind === 'brite' && toKind === 'fermenter') || (natural && stageIndex(natural) < stageIndex(batch.stage))) {
      return { kind: 'regress', stage: natural || 'Fermentation', fromKind, toKind };
    }
    const stage = defaultStageFor(toKind, batch.stage);
    return { kind: 'move', stage, fromKind, toKind };
  }
  const locationId = ctx.locationIdFor(target);
  if (locationId && ctx.isTaproomTarget?.(target)) {
    return {
      kind: 'invalid',
      reason: `${label(target)} is the taproom (sold, tax paid). Package into the brewery cooler, then send it with a Taproom Order.`,
    };
  }
  if (locationId) {
    const fromKind = vesselKind(source, ctx.equipment);
    // Barrel-aged beer must blend into a brite before packaging (Todd).
    if (fromKind === 'barrel' || batch.stage === 'Aging') {
      return { kind: 'invalid', reason: BLEND_BEFORE_PACKAGE };
    }
    return { kind: 'package', locationId };
  }
  return { kind: 'invalid', reason: `${label(target)} isn't linked to an inventory location` };
}

/** Topmost shape under a point (design coordinates). */
export function shapeAt(objects: DesignObject[], x: number, y: number, pad = 0): DesignObject | null {
  for (let i = objects.length - 1; i >= 0; i -= 1) {
    const o = objects[i];
    if (o.shape === 'circle') {
      const r = (o.radius || 30) + pad;
      if ((x - o.x) ** 2 + (y - o.y) ** 2 <= r * r) return o;
    } else {
      const w = o.width || 100;
      const h = o.height || 60;
      if (x >= o.x - pad && x <= o.x + w + pad && y >= o.y - pad && y <= o.y + h + pad) return o;
    }
  }
  return null;
}

/** Stage choices for a Move back: vessel stages up to (and including) the current one. */
export function regressStageOptions(current?: string | null): BatchStage[] {
  const cur = stageIndex(current);
  return VESSEL_STAGES.filter((s) => cur < 0 || stageIndex(s) <= cur);
}

/** Transfer volume must be a number > 0. */
export function parseTransferVolume(raw: string): { value?: number; error?: string } {
  if (!raw || !raw.trim()) return { error: 'Enter the volume transferred (bbl).' };
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return { error: 'Volume transferred must be more than 0 bbl.' };
  return { value: Math.round(n * 10000) / 10000 };
}

export const OPERATOR_ROLES = ['SuperAdmin', 'Admin', 'User', 'Production'];
export function canOperateFacility(role?: string | null): boolean {
  return !!role && OPERATOR_ROLES.includes(role);
}
